import { HttpClient } from '@angular/common/http';
import { ErrorHandler, Injectable, inject } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { environment } from 'environments/environment';
import { filter } from 'rxjs';

type UsageEvent = {
    kind: 'screen' | 'action' | 'error';
    name: string;
    detail?: string;
    context?: Record<string, unknown>;
};

/**
 * Tells the API which screens people open, what they do on them and what
 * breaks in the browser, so a company's use of Altura can be seen and a
 * failure can be looked at afterwards.
 *
 * Events leave in small batches. If the API cannot be reached they are
 * dropped: counting must never get in the way of working.
 */
@Injectable({ providedIn: 'root' })
export class UsageTrackerService {
    private readonly _http = inject(HttpClient);
    private readonly _router = inject(Router);
    private _queue: UsageEvent[] = [];
    private _timer: ReturnType<typeof setTimeout> | null = null;

    start(): void {
        this._router.events
            .pipe(filter((e): e is NavigationEnd => e instanceof NavigationEnd))
            .subscribe((e) => this.track('screen', this.screenName(e.urlAfterRedirects)));

        // A hidden tab is about to stop running; send what is left.
        document.addEventListener('visibilitychange', () => {
            if (document.visibilityState === 'hidden') {
                this.flush();
            }
        });
    }

    track(kind: UsageEvent['kind'], name: string, detail?: string, context?: Record<string, unknown>): void {
        this._queue.push({ kind, name: name.slice(0, 160), detail: detail?.slice(0, 1500), context });

        if (this._queue.length >= 20) {
            this.flush();
        } else if (!this._timer) {
            this._timer = setTimeout(() => this.flush(), 10000);
        }
    }

    /** /trips/42?tab=1 and /trips/57 are the same screen: numbers and ids become :id, the query goes. */
    private screenName(url: string): string {
        return (url.split('?')[0].split('#')[0] || '/')
            .split('/')
            .map((part) => (/^\d+$/.test(part) || /^[0-9a-f-]{20,}$/i.test(part) ? ':id' : part))
            .join('/');
    }

    private flush(): void {
        if (this._timer) {
            clearTimeout(this._timer);
            this._timer = null;
        }

        if (this._queue.length === 0) {
            return;
        }

        const events = this._queue.splice(0, 50);

        // Not signed in: there is nobody to attribute it to, and the API would refuse it.
        this._http
            .post(`${environment.apiUrl}/api/v1/stats/events`, { events }, { headers: { 'X-Skip-Loading-Bar': '1' } })
            .subscribe({ error: () => undefined });
    }
}

/** Reports what breaks in the browser, and still lets the console show it. */
@Injectable()
export class UsageErrorHandler implements ErrorHandler {
    private readonly _tracker = inject(UsageTrackerService);

    handleError(error: unknown): void {
        console.error(error);

        const err = error as { message?: string; stack?: string } | undefined;
        this._tracker.track('error', (err?.message ?? String(error)).slice(0, 160), err?.stack?.slice(0, 1500), {
            url: window.location.pathname,
        });
    }
}
